/**
 * Button recipe. Defaults to the brand (Apple Blue) color scheme and the
 * Yaxle interactive type. Solid/outline/ghost/link derive from `colorScheme`,
 * so `<Button colorScheme="brand">` and the legacy `"partbay-blue"` alias both
 * render the new blue without inline hex.
 */
export const Button = {
	baseStyle: {
		fontFamily: 'body',
		fontWeight: 'semibold',
		borderRadius: 'md',
		letterSpacing: '0.01em',
		_focusVisible: {
			boxShadow: 'focus',
		},
	},
	variants: {
		outlineGray: {
			bg: 'transparent',
			color: 'gray.700',
			border: '1px solid',
			borderColor: 'gray.300',
			_hover: {
				bg: 'gray.50',
				borderColor: 'gray.400',
			},
			_active: {
				bg: 'gray.100',
			},
		},
		/**
		 * Neutral ghost — quiet controls that shouldn't read as brand actions
		 * (dismiss/close icon buttons, tertiary toolbar glyphs).
		 */
		ghostGray: {
			bg: 'transparent',
			color: 'fg.muted',
			_hover: {
				bg: 'bg.subtle',
				color: 'fg.default',
			},
			_active: {
				bg: 'bg.muted',
			},
		},
		/**
		 * Outlined field trigger — looks like a quiet input (search, command
		 * palette). Semantic tokens only; keep muted in every state so it
		 * never reads as a brand action.
		 */
		outlineSubtle: {
			bg: 'bg.surface',
			color: 'fg.muted',
			border: '1px solid',
			borderColor: 'border.default',
			fontWeight: 'normal',
			_hover: {
				bg: 'bg.subtle',
				borderColor: 'border.strong',
				color: 'fg.muted',
			},
			_active: {
				bg: 'bg.muted',
				color: 'fg.muted',
			},
		},
	},
	sizes: {
		action: {
			h: 9,
			minW: 9,
			fontSize: 'sm',
			px: 3.5,
		},
		filter: {
			h: 7,
			minW: 7,
			fontSize: 'xs',
			px: 2.5,
		},
		/** Compact page-toolbar controls (filters, refresh, table settings). */
		toolbar: {
			h: 8,
			minW: 8,
			fontSize: 'sm',
			px: 3,
		},
	},
	defaultProps: {
		colorScheme: 'brand',
	},
} as const;

export default Button;
